import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, AlertTriangle, FileText, CloudUpload } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { computeSetupChecklist } from "../../lib/setupChecklist";
import ProjectHeader from "../../components/ProjectHeader";

/**
 * Iter-158.0 — Project home (beta).
 *
 * Shows shared scene/character counts and activated departments.
 * Iter-158.7.2 — the project-level "Canonical Script" upload widget
 * has been RETIRED. Beta now exposes exactly one canonical script
 * ingestion surface: the episodic slot endpoints inside each
 * department workspace (Hair & Makeup → Script Setup). Uploading
 * from here previously wrote scenes with a bespoke `beta_multidept`
 * schema that then coexisted with the Episode 1 tree in H&M,
 * producing the 57 + 57 = 114 duplication in the MT walkthrough.
 * The card is now a read-only pointer.
 */
const ALL_DEPTS = [
    { key: "hair_makeup", label: "Hair & Makeup" },
    { key: "art", label: "Art Department" },
    { key: "costume", label: "Costume" },
    { key: "vfx_ai", label: "VFX & AI" },
    // Iter-158.13.2.20d — placeholder Departments. Activation flows
    // through the same generic beta_departments row + scaffold view
    // (`BetaDepartment.jsx`) that Art / Costume / VFX use today. No
    // new schema, no new features. They exist so the concepts are
    // represented alongside the other Departments while the existing
    // development plan continues.
    { key: "production_workflow", label: "Production Workflow" },
    { key: "editorial_workflow", label: "Editorial Workflow" },
];

export default function BetaProject() {
    const { projectId } = useParams();
    const nav = useNavigate();
    const [state, setState] = useState({ project: null, script: null });
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(null);
    // Iter-158.13.2.20e.16 — backup status for the "Finish setting
    // up your Production" checklist card. Mirrors the derivation in
    // `ProjectHeader.loadBackupStatus`: null while loading; "none"
    // when no connection is configured; "paused" when configured
    // but disabled; "active" when configured and enabled. Failures
    // (e.g. transient 5xx) leave the value at null — the setup card
    // then treats it as still-missing, which is the safest default.
    const [backupStatus, setBackupStatus] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/projects/${projectId}`);
            setState({
                project: data?.project ?? null,
                script: data?.script ?? null,
            });
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav("/beta");
        } finally {
            setLoading(false);
        }
    }, [projectId, nav]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Iter-158.13.2.20e.16 — pull the Production's backup-connection
    // status so the setup checklist card can reflect real state
    // without any new endpoint / persistence flag. Runs Owner/Admin-
    // only (backend already 403s non-admins, so we simply swallow
    // the error and leave `backupStatus=null`, which the checklist
    // treats as missing). Refetches whenever the projectId changes.
    useEffect(() => {
        if (!projectId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(
                    `/beta/projects/${projectId}/backup/connection`,
                );
                if (cancelled) return;
                if (!data?.connection) setBackupStatus("none");
                else if (!data.connection.enabled) setBackupStatus("paused");
                else setBackupStatus("active");
            } catch {
                if (!cancelled) setBackupStatus(null);
            }
        })();
        return () => { cancelled = true; };
    }, [projectId]);

    const activate = async (dept_key) => {
        setActivating(dept_key);
        try {
            await api.post(`/beta/projects/${projectId}/departments`, { dept_key });
            await refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setActivating(null);
        }
    };

    if (loading) {
        // Iter-158.13.2.20f.33c — inherit the shared application
        // background (body → bg-background) so Light mode renders the
        // established off-white canvas and Dark mode the established
        // mid dark-grey. `bg-neutral-950` previously produced pure
        // black on dark and (via the light-mode remap in index.css)
        // white on light — both breaking the app-wide surface treatment
        // Production Management shares with every other page.
        return <div className="min-h-screen text-neutral-500 p-8 text-sm">Loading…</div>;
    }

    const proj = state.project || {};
    const active = new Set(proj.departments || []);
    const hmActive = active.has("hair_makeup");
    // Iter-158.13.2.12 — role-aware Hub tiles. `isActivated`
    // describes production state only (does the dept doc exist);
    // `canEnter(dept_key)` describes the caller's entitlement to
    // enter that department. Explicit dept membership drives
    // non-owner entry; Owner keeps the pre-existing owner_override.
    // Production Admins see every activated department for
    // oversight but cannot silently enter departments they don't
    // belong to — that path is a 403 dept_membership_required.
    const callerRole = proj.role || null;
    const deptMemberships = proj.dept_memberships || {};
    const canEnter = (dept_key) =>
        callerRole === "owner" || Boolean(deptMemberships[dept_key]);

    return (
        <div data-testid="beta-project" className="min-h-screen">
            {/* Iter-158.13.2.20f.33c — shell background inherits from
                the shared application `<body>` (`bg-background` /
                `text-foreground`) so Production Management matches
                every other application page in both themes. The
                previous `bg-neutral-950 text-neutral-100` combo
                remapped to `bg-card` (white) in light mode via
                `index.css` and rendered pure black in dark mode —
                Alex's UAT flagged both as inconsistent with the
                established application surface. */}
            {/* Iter-158.13.2.20f.28 — Production landing now uses the
                shared canonical `<ProjectHeader>` — the same header
                already mounted by Project Overview, Production
                Management (`ProjectSettings.jsx`), and the Department
                working surfaces. Reuse — do not create a second
                header. `ProjectHeader` provides Home / All My
                Projects, project switcher, Search, Upload,
                notifications/warnings, unassigned inbox, and the
                Settings cog; back-behaviour is handled by the shared
                `goBack` inside `TopBar`. The previous standalone
                `<header>` (bespoke "← My projects" link + h1 +
                "PRODUCTION" chip) has been removed to keep the
                surface consistent with every other Production
                surface. The landing-page destinations and hierarchy
                below (Script → Production Management → Documents →
                Schedule → Departments) are UNCHANGED — this
                iteration only harmonises the header. */}
            <ProjectHeader projectName={proj.name} subtitle="Production" />

            <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-6 pb-10">
                <header
                    data-testid="beta-project-hero"
                    className="mb-6"
                >
                    <div className="flex items-baseline gap-3">
                        <h1 data-testid="beta-project-name" className="text-2xl sm:text-3xl font-semibold">
                            {proj.name}
                        </h1>
                        <span
                            data-testid="beta-project-destination-label"
                            className="text-[11px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Production
                        </span>
                    </div>
                    <p className="text-[12px] text-neutral-500 mt-1">
                        {proj.scene_count} scenes · {proj.character_count} characters
                    </p>
                    <p className="text-[12px] text-neutral-400 mt-2 max-w-xl">
                        You&apos;re at the production level — invite the right people into each department; you never have to belong to a department to oversee it.
                    </p>
                </header>

                <div className="space-y-6">
                {/* Iter-158.13.2.20e.16 — "Finish setting up your
                    Production" checklist. Persistent, Owner/Admin
                    only, driven by real state (`state.script` +
                    backup connection). Disappears automatically once
                    Script exists AND Backup is active. A paused
                    backup keeps the card visible with a calm needs-
                    attention row so the producer sees the situation
                    from the front-door instead of hunting for it.
                    No new engine, no persistence flag, no navigation
                    redesign — one card that points at the two
                    existing Production-scope routes:
                        /beta/projects/:pid/script-setup
                        /beta/projects/:pid/backups */}
                {(() => {
                    const checklist = computeSetupChecklist({
                        hasScript: Boolean(state.script),
                        backupStatus,
                        callerRole,
                    });
                    if (!checklist.showCard) return null;
                    return (
                        <section
                            data-testid="beta-project-setup-checklist-card"
                            className="rounded-xl border border-sky-500/30 bg-sky-500/[0.12] p-4"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <h2 className="text-[11px] uppercase tracking-[0.24em] text-sky-300 font-semibold">
                                    Finish setting up your Production
                                </h2>
                            </div>
                            <p className="text-[12px] text-foreground mt-2 leading-relaxed">
                                Two production-level things worth doing now. You can come back to them any time from here.
                            </p>

                            {/* Row 1 — Canonical Script */}
                            <div
                                data-testid="setup-row-script"
                                data-complete={checklist.scriptComplete ? "true" : "false"}
                                className="mt-4 flex items-start gap-3 rounded-lg border border-border bg-card p-3"
                            >
                                <span className="h-8 w-8 shrink-0 rounded-md bg-primary/15 text-primary flex items-center justify-center">
                                    {checklist.scriptComplete ? (
                                        <CheckCircle2 size={16} data-testid="setup-row-script-check" />
                                    ) : (
                                        <FileText size={16} />
                                    )}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-foreground font-semibold">
                                        Canonical script
                                    </div>
                                    <p className="text-[12px] text-muted-foreground mt-0.5">
                                        {checklist.scriptComplete
                                            ? "Your canonical script is uploaded and parsed."
                                            : "Upload or paste your script. Scenes and characters become the shared spine of every department."}
                                    </p>
                                </div>
                                {checklist.scriptComplete ? null : (
                                    <Link
                                        data-testid="setup-row-script-cta"
                                        to={`/beta/projects/${projectId}/script-setup`}
                                        className="tap px-3 h-8 inline-flex items-center rounded-full text-[11px] uppercase tracking-[0.2em] bg-amber-500 text-neutral-950 border border-amber-500 hover:bg-amber-400 font-semibold shrink-0"
                                    >
                                        Open Script Setup
                                        <ArrowRight size={12} className="ml-1" />
                                    </Link>
                                )}
                            </div>

                            {/* Row 2 — Back Up Your Production */}
                            <div
                                data-testid="setup-row-backup"
                                data-complete={checklist.backupComplete ? "true" : "false"}
                                data-paused={checklist.backupPaused ? "true" : "false"}
                                className="mt-3 flex items-start gap-3 rounded-lg border border-border bg-card p-3"
                            >
                                <span
                                    className={`h-8 w-8 shrink-0 rounded-md flex items-center justify-center ${
                                        checklist.backupPaused
                                            ? "bg-amber-500/15 text-amber-600 dark:text-amber-200"
                                            : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-200"
                                    }`}
                                >
                                    {checklist.backupPaused ? (
                                        <AlertTriangle size={16} data-testid="setup-row-backup-paused-icon" />
                                    ) : (
                                        <CloudUpload size={16} />
                                    )}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-foreground font-semibold">
                                        Back Up Your Production
                                    </div>
                                    <p className="text-[12px] text-muted-foreground mt-0.5">
                                        {checklist.backupPaused
                                            ? "Backup is configured but currently paused. Reopen the Backup surface to resume."
                                            : "Connect Dropbox (or another provider) so every file you upload is mirrored to your own storage."}
                                    </p>
                                </div>
                                <Link
                                    data-testid="setup-row-backup-cta"
                                    to={`/beta/projects/${projectId}/backups`}
                                    className={`tap px-3 h-8 inline-flex items-center rounded-full text-[11px] uppercase tracking-[0.2em] shrink-0 font-semibold ${
                                        checklist.backupPaused
                                            ? "bg-amber-500 text-neutral-950 border border-amber-500 hover:bg-amber-400"
                                            : "bg-emerald-500 text-neutral-950 border border-emerald-500 hover:bg-emerald-400"
                                    }`}
                                >
                                    {checklist.backupPaused ? "Resume Backup" : "Set Up Backup"}
                                    <ArrowRight size={12} className="ml-1" />
                                </Link>
                            </div>

                            <p className="text-[11px] text-muted-foreground mt-3">
                                This card disappears automatically once your script is uploaded and backup is active.
                            </p>
                        </section>
                    );
                })()}

                {/* Canonical script — Iter-158.13.2.20e.1+.20e.2:
                    Production-owned. Displayed summary is loaded
                    from `beta_scripts` (episodic slot) via the Hub
                    GET. Access gated by `can_manage_script` — Owner
                    always; other Production members with an explicit
                    `manage_script` permission flag. Legacy "owner
                    only" copy has been retired. */}
                <section
                    data-testid="beta-project-script-card"
                    className="rounded-xl border border-border bg-card p-4"
                >
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Canonical script</h2>
                    {state.script ? (
                        <div className="mt-3">
                            <p data-testid="beta-project-script-title" className="text-sm text-foreground">
                                {state.script.title || (state.script.episode ? `Episode ${state.script.episode}` : "Script")}
                            </p>
                            <p className="text-[12px] text-muted-foreground mt-0.5">
                                {(state.script.chars || 0).toLocaleString()} characters
                                {state.script.parsed_at ? ` · parsed at ${state.script.parsed_at.slice(0, 16).replace("T", " ")}` : ""}
                            </p>
                            <p className="text-[12px] text-emerald-600 dark:text-emerald-400 mt-2">
                                {proj.scene_count} shared scenes · {proj.character_count} shared characters — visible to every department.
                            </p>
                        </div>
                    ) : (
                        <p
                            data-testid="beta-project-script-empty"
                            className="mt-2 text-sm text-muted-foreground"
                        >
                            No script uploaded yet. Open Script Setup to add the canonical Production script.
                        </p>
                    )}
                    <div className="mt-4">
                        <button
                            type="button"
                            data-testid="beta-project-script-goto-setup"
                            onClick={() => {
                                if (state.project?.can_manage_script) {
                                    nav(`/beta/projects/${projectId}/script-setup`);
                                } else {
                                    toast.info(
                                        "Canonical Script is Production-owned. Ask the Production Owner to grant you Manage Script from Team & Access.",
                                    );
                                }
                            }}
                            className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] font-semibold bg-amber-500 text-neutral-950 border border-amber-500 hover:bg-amber-400"
                        >
                            {state.project?.can_manage_script
                                ? "Open Script Setup"
                                : "View Script Setup"}
                        </button>
                    </div>
                </section>

                {/* Iter-158.13.2.20e.2 — Production Team & Access.
                    Canonical Team & Access moved to Production level.
                    The FE surface is the same shared `ProjectSettings`
                    page, mounted at `/beta/projects/:pid/settings`
                    with `apiBase="/beta/projects"` so every downstream
                    T&A endpoint hits the Production factory mount.
                    See App.js `<Route path="/beta/projects/:projectId/
                    settings">`.

                    Iter-158.13.2.20f.26b — Card RE-LABELLED as
                    Production Management (was "Team & Access"). Human
                    UAT on iPhone Safari (Tree Frog) confirmed this
                    single card already opens the whole Production
                    Management surface — Team & Access lives inside it
                    alongside Governance / Ownership & Lifecycle / Legal.
                    The old label duplicated the concept when a
                    subordinate "Production Management" card also lived
                    below Department Workspaces (that duplicate has been
                    removed in the same iteration). Route, testids, and
                    solid-fill sky-500 CTA styling are UNCHANGED so
                    every existing test / navigation contract holds. */}
                <section
                    data-testid="beta-project-team-access-card"
                    className="rounded-xl border border-border bg-card p-4"
                >
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Production Management</h2>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        Team &amp; Access, roles and per-member permissions, ownership, storage &amp; protection, restore points, full Production export, archive &amp; delete, legal &amp; governance. New joiners land as viewers by default; promote them and grant Manage Script explicitly.
                    </p>
                    <div className="mt-4">
                        <Link
                            data-testid="beta-project-team-access-link"
                            to={`/beta/projects/${projectId}/settings`}
                            className="tap px-4 h-9 inline-flex items-center rounded-full text-[12px] uppercase tracking-[0.2em] font-semibold bg-sky-500 text-white border border-sky-500 hover:bg-sky-400"
                        >
                            Open Production Management
                        </Link>
                    </div>
                </section>

                {/* Iter-158.13.2.20e.10 — Production Documents.
                    Canonical Production Documents + Supervisor Notes.
                    Backed by the shared `notes_docs.py` factory mounted
                    at `/api/beta/projects/{pid}/…` with scope stamp
                    `{scope: "production", dept_key: null}`. FE reuses
                    the existing `SupervisorNotesPage` under
                    `ApiBaseProvider value="/beta/projects"`. Route
                    wired in App.js at
                    `/beta/projects/:projectId/supervisor-notes`. */}
                <section
                    data-testid="beta-project-documents-card"
                    className="rounded-xl border border-border bg-card p-4"
                >
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Documents &amp; Notes</h2>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        Canonical Production Documents and Supervisor Notes — the shared timeline every department reads from.
                    </p>
                    <div className="mt-4">
                        <Link
                            data-testid="beta-project-documents-link"
                            to={`/beta/projects/${projectId}/supervisor-notes`}
                            className="tap px-4 h-9 inline-flex items-center rounded-full text-[12px] uppercase tracking-[0.2em] font-semibold bg-violet-500 text-white border border-violet-500 hover:bg-violet-400"
                        >
                            Open Documents
                        </Link>
                    </div>
                </section>

                {/* Iter-158.13.2.20e.10 — Production Schedule.
                    Canonical Production Schedule. Backed by the
                    shared `schedules.py` factory mounted at
                    `/api/beta/projects/{pid}/schedules` with scope
                    stamp `{scope: "production", dept_key: null}`.
                    FE reuses the existing `SchedulePage` under
                    `ApiBaseProvider value="/beta/projects"`. Route
                    wired in App.js at
                    `/beta/projects/:projectId/schedule`. */}
                <section
                    data-testid="beta-project-schedule-card"
                    className="rounded-xl border border-border bg-card p-4"
                >
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Schedule</h2>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        Canonical production shooting schedule — days, scenes, times. Shared across every department.
                    </p>
                    <div className="mt-4">
                        <Link
                            data-testid="beta-project-schedule-link"
                            to={`/beta/projects/${projectId}/schedule`}
                            className="tap px-4 h-9 inline-flex items-center rounded-full text-[12px] uppercase tracking-[0.2em] font-semibold bg-emerald-500 text-neutral-950 border border-emerald-500 hover:bg-emerald-400"
                        >
                            Open Schedule
                        </Link>
                    </div>
                </section>

                {/* Departments */}
                <section className="rounded-xl border border-border bg-card p-4">
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Department workspaces</h2>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        Every department reads from the same shared scenes and characters — activating a department never duplicates data.
                    </p>
                    <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {ALL_DEPTS.map((d) => {
                            const isActivated = active.has(d.key);
                            const mayEnter = canEnter(d.key);
                            return (
                                <li key={d.key} className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2">
                                    <div className="min-w-0">
                                        <p className="text-sm text-neutral-100">{d.label}</p>
                                        <p className="text-[11px] text-neutral-500">
                                            {isActivated
                                                ? mayEnter
                                                    ? "Active"
                                                    : "Active · you're not a member"
                                                : "Not activated"}
                                        </p>
                                    </div>
                                    {isActivated && mayEnter ? (
                                        <Link
                                            data-testid={`beta-dept-open-${d.key}`}
                                            to={d.key === "hair_makeup"
                                                ? `/beta/hm-app/projects/${projectId}`
                                                : `/beta/projects/${projectId}/departments/${d.key}`}
                                            className="tap px-3 h-8 inline-flex items-center rounded-full text-[11px] uppercase tracking-[0.2em] text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/15"
                                        >
                                            Open
                                        </Link>
                                    ) : isActivated && !mayEnter ? (
                                        /* Iter-158.13.2.12 — Production Admin
                                           oversight-only tile. Not clickable;
                                           the caller can see the dept exists
                                           and has activity but cannot enter
                                           because they have no explicit
                                           membership. Invites/oversight belong
                                           in the Hub, not in the dept. */
                                        <span
                                            data-testid={`beta-dept-members-only-${d.key}`}
                                            aria-disabled="true"
                                            title="Members only. Invite yourself from Team & Access to enter this department."
                                            className="px-3 h-8 inline-flex items-center rounded-full text-[11px] uppercase tracking-[0.2em] text-neutral-500 border border-neutral-800 bg-neutral-950 cursor-not-allowed select-none"
                                        >
                                            Members only
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid={`beta-dept-activate-${d.key}`}
                                            disabled={activating === d.key}
                                            onClick={() => activate(d.key)}
                                            className="tap px-3 h-8 rounded-full text-[11px] uppercase tracking-[0.2em] text-neutral-200 border border-neutral-700 hover:bg-neutral-800 disabled:opacity-50"
                                        >
                                            {activating === d.key ? "…" : "Activate"}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {/* Iter-158.13.2.20f.26b — Both the subordinate
                    "Production Management" card (previously here,
                    duplicate entrance to /beta/projects/:pid/settings
                    already reachable via the renamed card above) AND
                    the standalone "Leave this Production" card
                    (previously below it, wrong information architecture
                    per human UAT) have been DELETED.

                    Effect: Department Workspaces is now the final
                    normal section on this Production landing surface,
                    matching the locked product decision:
                       Canonical Script → Production Management →
                       Documents & Notes → Schedule → Department
                       Workspaces.
                    Non-owner Leave has been RESTORED to its canonical
                    home inside Production Management → Governance /
                    Ownership & Lifecycle (see the settings-leave-
                    project card in ProjectSettings.jsx, restored in
                    the same iteration).

                    The prior subordinate section
                    `data-testid="beta-project-production-management-
                    card"` and its Open link
                    `beta-project-production-management-link` are
                    removed. The prior non-owner leave section
                    `data-testid="beta-project-leave-card"` with its
                    `beta-project-leave-btn` button is removed; the
                    underlying `/beta/hm-app/projects/:pid/leave`
                    endpoint is UNTOUCHED and still reached by the
                    MyProjects hub-row menu (beta-hub-leave-*), the
                    MyProjects dept-row menu (beta-dept-leave-*), and
                    the restored settings-leave-project card. */}
                </div>
            </div>
        </div>
    );
}
